import { Injectable } from '@angular/core';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { FiImageSize, FileType } from '@fi-shared/types';
import { ITextItemMediaModel, ITextItemMediaModelFile } from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { TextItemMediaClickActionType } from '@fi-shared/types/text-item-media-click-action-type';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { findClosest } from '@fi-shared/utils';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { AppTextService } from '@fi-shared/app-text';
import {
    getMediaEl,
    getMediaAnchorEl,
    getVimeoPreviewImgContainer,
    getVimeoUnavailableContainer,
    getMediaWrapperAnchorEl,
    replaceVimeoNotAvailableLabelsInHtml,
} from './text-editor-html-element';
import { FiVimeoService } from '../media-bank/fi-vimeo/fi-vimeo.service';

@Injectable()
export class TextEditorInsertMediaService {
    constructor(
        private linkToTypes: LinkToTypesService,
        private mediaStorageService: FiMediaStorageService,
        private textItemHelperService: FiTextItemHelperService,
        private appTextService: AppTextService,
        private vimeoService: FiVimeoService,
    ) {}

    async createNodesFromResult(mediaBankFileRefs: IMediaBankFileReference[]): Promise<HTMLElement[]> {
        const elements: HTMLElement[] = [];
        for (const mediaBankFileRef of mediaBankFileRefs) {
            const textItemModel = this.convertMediaBankFileReferenceToTextItemMediaModel(mediaBankFileRef);
            const resultHTML = await this.prepareResultHtmlElement(textItemModel, mediaBankFileRef);
            elements.push(resultHTML, document.createElement('br'));
        }
        return elements;
    }

    private convertMediaBankFileReferenceToTextItemMediaModel(file: IMediaBankFileReference): ITextItemMediaModel {
        const textItemModel: ITextItemMediaModel = {
            file: {
                MediaBankFileId: file.MediaBankFileId,
                FullName: file.PhysicalFileName + file.Extension,
                ChangedOn: file.ChangedOn,
                Name: file.Name,
                FileType: file.FileType,
                StorageType: file.StorageType,
                PhysicalFileName: file.PhysicalFileName,
                Extension: file.Extension,
            },
            isNewUpload: file.IsNewUpload,
            hideFromMediaBank: file.HideFromMediaBank,
        };

        textItemModel.displayAs =
            this.isMediaItemFileType(file.FileType) || file.StorageType === MediaBankStorageType.Vimeo
                ? FiDisplayAs.Image.Id
                : FiDisplayAs.DownloadableFile.Id;

        textItemModel.onClickActionType =
            textItemModel.displayAs === FiDisplayAs.Image.Id
                ? TextItemMediaClickActionType.EnlargedView
                : TextItemMediaClickActionType.DownloadFile;

        return textItemModel;
    }

    private async prepareResultHtmlElement(
        textItemModel: ITextItemMediaModel,
        mediaBankFileReference: IMediaBankFileReference,
    ): Promise<HTMLElement> {
        if (textItemModel.displayAs === FiDisplayAs.DownloadableFile.Id) {
            const title = this.getDownloadableFileTitle(textItemModel);
            const url = await this.mediaStorageService.getMediaFileUrlForDownload(textItemModel.file);
            if (!url) {
                throw new Error('No URL for download');
            }
            const anchor = getMediaWrapperAnchorEl(url.link, title, true);
            anchor.setAttribute('ccm-text-item-media-model', JSON.stringify(textItemModel));
            return anchor;
        }

        const resultElement = await this.getInsertItemHTML(textItemModel, mediaBankFileReference);
        resultElement.setAttribute('ccm-text-item-media-model', JSON.stringify(textItemModel));
        return resultElement;
    }

    private getDownloadableFileTitle(textItemModel: ITextItemMediaModel): string {
        if (textItemModel.displayTitle) return textItemModel.displayTitle;
        const { FullName, Name } = textItemModel.file;
        const parts = FullName.split('.');
        return parts.length > 1 ? `${Name}.${parts.pop()}` : Name;
    }

    private getVimeoPlaceholderHTML(
        textItemModelFile: ITextItemMediaModelFile,
        isVideoAvailable: boolean,
    ): HTMLElement {
        if (isVideoAvailable) {
            const mediaUrl = this.mediaStorageService.getMediaFileUrlByFileName(
                textItemModelFile.FullName,
                FiImageSize.Medium,
                textItemModelFile.ChangedOn,
            );
            return getVimeoPreviewImgContainer(mediaUrl);
        }
        return getVimeoUnavailableContainer(
            this.appTextService.get('file.video_processing_text'),
            this.appTextService.get('file.video_processing_description'),
        );
    }

    public replaceVimeoNotAvailableLabelsInHtml(editorEl: HTMLElement): void {
        replaceVimeoNotAvailableLabelsInHtml(
            editorEl,
            this.appTextService.get('file.video_processing_text'),
            this.appTextService.get('file.video_processing_description'),
        );
    }

    private async getInsertItemHTML(
        textItemModel: ITextItemMediaModel,
        mediaBankFileReference: IMediaBankFileReference,
    ): Promise<HTMLElement> {
        if (textItemModel.file.StorageType === MediaBankStorageType.Vimeo) {
            const videoData = await this.vimeoService.getVideoInfo(mediaBankFileReference.PhysicalFileName);
            return this.getVimeoPlaceholderHTML(textItemModel.file, videoData.isAvailable);
        }

        const itemEl = this.getMediaItemHTML(textItemModel.file) || this.getMediaAnchorHTML(textItemModel.file);

        if (textItemModel.linkTo && this.isMediaItemFileType(textItemModel.file.FileType)) {
            const href = this.linkToTypes.getUrlForItemLinkModel(textItemModel.linkTo);
            if (href) {
                return getMediaWrapperAnchorEl(href, itemEl, textItemModel.linkTo.Type === FiLinkToType.WebAddress);
            }
        }
        return itemEl;
    }

    private isMediaItemFileType(fileType: FileType): boolean {
        return fileType === FileType.Image || fileType === FileType.Movie;
    }

    private getMediaItemHTML(textItemMediaFile: ITextItemMediaModelFile): HTMLElement {
        const mediaUrl = this.mediaStorageService.getMediaFileUrlByFileName(
            textItemMediaFile.FullName,
            FiImageSize.Medium,
            textItemMediaFile.ChangedOn,
        );
        return getMediaEl(textItemMediaFile, mediaUrl);
    }

    private getMediaAnchorHTML(textItemMediaFile: ITextItemMediaModelFile): HTMLElement {
        const anchorSrc = this.mediaStorageService.getMediaFileUrlByFileName(
            textItemMediaFile.FullName,
            FiImageSize.Medium,
            textItemMediaFile.ChangedOn,
        );
        return getMediaAnchorEl(anchorSrc, textItemMediaFile.Name);
    }

    insertMediaIntoTextEditorRange(range: Range, selection: Selection, nodes: HTMLElement[]): void {
        if (nodes) {
            nodes.forEach((node) => range.insertNode(node));
        }
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);

        const el: HTMLElement = (range.startContainer as HTMLElement).closest('.text-editor');
        this.textItemHelperService.updateTextItemMediaImages(el);
        el.dispatchEvent(new Event('change'));
    }

    getRangeForMediaInsert(editableElement: HTMLElement, selection: Selection): Range {
        let range: Range | undefined;
        if (selection.rangeCount) {
            range = selection.getRangeAt(0);
        }

        if (!range || !findClosest(range.startContainer, '.text-editor')) {
            range = new Range();
            range.selectNodeContents(editableElement);
            range.collapse(false);
        }

        return range;
    }
}
